import { useState, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Camera, Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";

export default function PhotoUpload({ entityType, entityId, photoType = "general", photos = [], onPhotosChange }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({});
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newPhotos = [];
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post(
          `/upload?entity_type=${entityType}&entity_id=${entityId}&photo_type=${photoType}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        newPhotos.push(data);
        toast.success(`Uploaded ${file.name}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (onPhotosChange && newPhotos.length > 0) onPhotosChange();
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      toast.success("Photo removed");
      if (onPhotosChange) onPhotosChange();
    } catch { toast.error("Failed to delete"); }
  };

  const loadPreview = async (photo) => {
    if (previewUrls[photo.id]) return;
    try {
      const response = await api.get(`/files/${photo.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      setPreviewUrls((prev) => ({ ...prev, [photo.id]: url }));
    } catch (err) { console.error("Photo preview failed:", err); }
  };

  // Load previews for photos
  useState(() => {
    photos.forEach((p) => loadPreview(p));
  });

  return (
    <div className="space-y-3" data-testid={`photo-upload-${entityType}-${photoType}`}>
      {/* Upload Buttons */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="text-xs"
          data-testid={`upload-photo-btn-${photoType}`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          Upload
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
          className="text-xs"
          data-testid={`camera-btn-${photoType}`}
        >
          <Camera className="w-3.5 h-3.5 mr-1" /> Take Photo
        </Button>
      </div>

      {/* Photo Grid */}
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50">
              {previewUrls[photo.id] ? (
                <img
                  src={previewUrls[photo.id]}
                  alt={photo.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" onClick={() => loadPreview(photo)}>
                  <ImageIcon className="w-6 h-6 text-zinc-300" />
                </div>
              )}
              <button
                onClick={() => handleDelete(photo.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-photo-${photo.id}`}
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5">
                <p className="text-white text-[10px] truncate">{photo.original_filename}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-zinc-300 rounded-lg py-6 text-center">
          <ImageIcon className="w-8 h-8 text-zinc-300 mx-auto mb-1" />
          <p className="text-xs text-zinc-400">No photos yet</p>
        </div>
      )}
    </div>
  );
}
